import type { ReactNode } from 'react';

export interface RecordFact {
  label: string;
  value: ReactNode;
}

interface Props {
  /** Zustand der Zeile — Pille mit Text, nie Farbe allein (§2). */
  status: ReactNode;
  /** Die eine Zeile, an der man den Datensatz wiedererkennt. */
  identity: ReactNode;
  /** Technische Kennungen setzt der Aufrufer auf Mono — Fachbegriffe bleiben in der UI-Schrift. */
  identityMono?: boolean;
  facts: RecordFact[];
  /** Fehlertext o.ä. — steht ungekürzt, weil er der Grund des Besuchs ist. */
  note?: string | null;
  action?: ReactNode;
}

/**
 * Eine Tabellenzeile als gestapelte Karte — die Mobilkomposition der Forensik-Tabellen.
 *
 * <p>Die Tabellen unter `/system/*` liegen im Einstellungs-Layout und haben dort nur rund 714 px
 * Arbeitsbreite, auch auf einem 1440-px-Bildschirm. Der Mail-Ausgang brauchte 1013 px, seine
 * Aktionsspalte lag damit auf <em>jedem</em> Viewport hinter einem horizontalen Scroll. Unterhalb
 * der gemessenen Grenze ist die Tabelle deshalb keine Arbeitsfläche mehr, sondern diese Karte
 * (§9, gleiche Entscheidung wie C1 bei der Rechnungsliste). Sie steht hier statt im Feature,
 * weil System-, Channex- und Finanzflächen sie teilen.</p>
 */
export function RecordCard({ status, identity, identityMono = false, facts, note, action }: Props) {
  return (
    <li className="border-line bg-card shadow-card rounded-lg border p-4">
      <div className="flex flex-wrap items-start justify-between gap-2">
        <div
          className={
            identityMono
              ? 'text-ink wrap-break-word min-w-0 font-mono text-xs'
              : 'text-ink wrap-break-word min-w-0 font-serif text-base'
          }
        >
          {identity}
        </div>
        {status}
      </div>
      <dl className="mt-3 grid grid-cols-2 gap-x-4 gap-y-2 text-xs">
        {facts.map((fact) => (
          <div key={fact.label} className="min-w-0">
            <dt className="text-ink-faint text-[11px] uppercase tracking-wide">{fact.label}</dt>
            <dd className="text-ink wrap-break-word mt-0.5 tabular-nums">{fact.value}</dd>
          </div>
        ))}
      </dl>
      {note && <p className="text-sealwax wrap-break-word mt-3 text-xs">{note}</p>}
      {action && <div className="mt-3">{action}</div>}
    </li>
  );
}
